// SPDX-License-Identifier: AGPL-3.0-or-later

import {SoundType} from '@app/features/notification/utils/SoundUtils';
import {Platform} from '@app/features/platform/types/Platform';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as SoundCommands from '@app/features/ui/commands/SoundCommands';
import {getElectronAPI, isDesktop, isNativeMacOS} from '@app/features/ui/utils/NativeUtils';
import {CameraPermissionDeniedModal} from '@app/features/voice/components/alerts/CameraPermissionDeniedModal';
import {MicrophonePermissionDeniedModal} from '@app/features/voice/components/alerts/MicrophonePermissionDeniedModal';
import {getStreamKey} from '@app/features/voice/components/StreamKeys';
import VoiceConnectionManager from '@app/features/voice/engine/VoiceConnectionManager';
import {
	syncLocalVoiceStateWithServer,
	updateLocalParticipantFromRoom,
} from '@app/features/voice/engine/VoiceMediaEngineBridge';
import VoiceMediaStateCoordinator from '@app/features/voice/engine/VoiceMediaStateCoordinator';
import ActiveScreenShareSource from '@app/features/voice/state/ActiveScreenShareSource';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import MediaPermission from '@app/features/voice/state/MediaPermission';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {
	getBackupCodecForPrimary,
	resolveScreenShareContentHint,
	selectOptimalScreenShareCodec,
} from '@app/features/voice/utils/CodecCapabilityDetector';
import {captureVirtmicAudioTrack, disarmVirtmic} from '@app/features/voice/utils/LinuxScreenShareAudio';
import {disarmNativeAudio} from '@app/features/voice/utils/NativeAudioCaptureBridge';
import {ensureNativePermission} from '@app/features/voice/utils/NativePermissions';
import {ScreenRecordingPermissionDeniedError} from '@app/features/voice/utils/ScreenRecordingPermissionDeniedError';
import {
	createLocalAudioTrack,
	createLocalVideoTrack,
	type LocalAudioTrack,
	type LocalParticipant,
	type LocalVideoTrack,
	type Room,
	type ScreenShareCaptureOptions,
	Track,
	type TrackPublishOptions,
	type VideoCodec,
} from 'livekit-client';

import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('VoiceScreenShareManager');
function getPreferredScreenShareCodec(): VideoCodec {
	return selectOptimalScreenShareCodec(VoiceSettings.getPreferredScreenShareCodec());
}
export interface DeviceScreenShareCaptureOptions {
	videoDeviceId?: string;
	audioDeviceId?: string;
	resolution?: ScreenShareCaptureOptions['resolution'];
	sendUpdate?: boolean;
	playSound?: boolean;
}
interface CapturedScreenShareTracks {
	videoTrack: MediaStreamTrack;
	audioTrack?: MediaStreamTrack;
}
interface SimulcastTrackInfoLike {
	mediaStreamTrack: MediaStreamTrack;
	sender?: RTCRtpSender;
}
function stopMediaTrack(track: MediaStreamTrack | undefined): void {
	if (!track) {
		return;
	}
	try {
		track.stop();
	} catch (error) {
		logger.warn('Failed to stop unused screen share media track', {error});
	}
}
class VoiceScreenShareManager {
	isScreenSharePending = false;
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}
	getIsScreenSharePending(): boolean {
		return this.isScreenSharePending;
	}
	private getEffectivePublishOptions(
		enabled: boolean,
		publishOptions?: TrackPublishOptions,
	): TrackPublishOptions | undefined {
		if (!enabled) {
			return publishOptions;
		}
		const preferredVideoCodec = publishOptions?.videoCodec ?? getPreferredScreenShareCodec();
		const backupCodec = publishOptions?.backupCodec ?? getBackupCodecForPrimary(preferredVideoCodec);
		return {
			...publishOptions,
			videoCodec: preferredVideoCodec,
			backupCodec,
		};
	}
	private applyScreenShareContentHint(participant: LocalParticipant): void {
		const publication = participant.getTrackPublication(Track.Source.ScreenShare);
		const track = publication?.videoTrack;
		if (!track) {
			return;
		}
		const hint = resolveScreenShareContentHint(VoiceSettings.getScreenShareContentHint());
		try {
			track.mediaStreamTrack.contentHint = hint;
		} catch (error) {
			logger.warn('Failed to apply screen share content hint', {error, hint});
		}
	}
	private getLocalStreamKey(): string {
		const {guildId, channelId, connectionId} = VoiceConnectionManager.connectionState;
		if (!connectionId) {
			const error = new Error('Cannot sync screen share watcher without an active voice connection');
			logger.error('Missing connection id when deriving local stream key', {
				connectionState: VoiceConnectionManager.connectionState,
			});
			throw error;
		}
		return getStreamKey(guildId, channelId, connectionId);
	}
	private syncLocalScreenShareAudioState(participant: LocalParticipant, enabled: boolean): void {
		const hasAudioTrack = Boolean(participant.getTrackPublication(Track.Source.ScreenShareAudio));
		LocalVoiceState.updateSelfStreamAudio(enabled && hasAudioTrack);
	}
	private syncPersistedScreenShareAudioPreference(participant: LocalParticipant): void {
		const screenShareAudioPublication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		const muteStreamAudio = !(screenShareAudioPublication && !screenShareAudioPublication.isMuted);
		if (VoiceSettings.getMuteStreamAudio() !== muteStreamAudio) {
			VoiceSettings.updateSettings({muteStreamAudio});
		}
	}
	async cleanupLingeringScreenShareAudio(participant: LocalParticipant): Promise<void> {
		void getElectronAPI()?.virtmic?.stop();
		disarmVirtmic();
		disarmNativeAudio();
		ActiveScreenShareSource.clear();
		const publication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		const track = publication
			? (('track' in publication ? publication.track : undefined) ??
				('audioTrack' in publication ? publication.audioTrack : undefined))
			: undefined;
		if (!track) {
			return;
		}
		try {
			await participant.unpublishTrack(track);
		} catch (error) {
			logger.warn('Failed to unpublish lingering screen share audio track', {error});
		}
		try {
			track.stop();
		} catch (error) {
			logger.warn('Failed to stop lingering screen share audio track', {error});
		}
	}
	private syncLocalStreamWatchState(enabled: boolean): void {
		const streamKey = this.getLocalStreamKey();
		const current = LocalVoiceState.getViewerStreamKeys();
		if (enabled) {
			if (current.includes(streamKey)) return;
			const updated = [...current, streamKey];
			LocalVoiceState.updateViewerStreamKeys(updated);
			syncLocalVoiceStateWithServer({viewer_stream_keys: updated});
			return;
		}
		if (!current.includes(streamKey)) {
			logger.debug('Local stream watcher already absent while disabling screen share', {
				current,
				expected: streamKey,
			});
			return;
		}
		const updated = current.filter((k) => k !== streamKey);
		LocalVoiceState.updateViewerStreamKeys(updated);
		syncLocalVoiceStateWithServer({viewer_stream_keys: updated});
	}
	private getDisplayMediaOptions(options?: ScreenShareCaptureOptions): DisplayMediaStreamOptions {
		let videoConstraints: MediaTrackConstraints | boolean = options?.video ?? true;
		const resolution = options?.resolution;
		if (resolution && resolution.width > 0 && resolution.height > 0) {
			videoConstraints = typeof videoConstraints === 'boolean' ? {} : videoConstraints;
			videoConstraints = {
				...videoConstraints,
				width: {ideal: resolution.width},
				height: {ideal: resolution.height},
				frameRate: resolution.frameRate,
			};
		}
		const base = typeof videoConstraints === 'boolean' ? {} : videoConstraints;
		videoConstraints = {
			...base,
			cursor: VoiceSettings.getStreamHideCursor() ? 'never' : 'always',
		} as MediaTrackConstraints;
		return {
			audio: options?.audio ?? false,
			video: videoConstraints,
			controller: options?.controller,
			selfBrowserSurface: options?.selfBrowserSurface,
			surfaceSwitching: options?.surfaceSwitching,
			systemAudio: options?.systemAudio,
			preferCurrentTab: options?.preferCurrentTab,
		} as DisplayMediaStreamOptions;
	}
	private async createDisplayScreenShareTracks(
		options?: ScreenShareCaptureOptions,
	): Promise<CapturedScreenShareTracks> {
		if (!navigator.mediaDevices.getDisplayMedia) {
			throw new Error('getDisplayMedia not supported');
		}
		const stream = await navigator.mediaDevices.getDisplayMedia(this.getDisplayMediaOptions(options));
		const videoTrack = stream.getVideoTracks()[0];
		if (!videoTrack) {
			stream.getTracks().forEach(stopMediaTrack);
			throw new Error('No video track found in screen share capture');
		}
		if (options?.contentHint) {
			videoTrack.contentHint = options.contentHint;
		}
		return {
			videoTrack,
			audioTrack: stream.getAudioTracks()[0],
		};
	}
	private getDeviceMediaConstraints(
		options: DeviceScreenShareCaptureOptions | undefined,
		useExactDeviceId: boolean,
	): MediaStreamConstraints {
		const resolution = options?.resolution;
		const videoConstraints: MediaTrackConstraints = {};
		if (options?.videoDeviceId && options.videoDeviceId !== 'default') {
			videoConstraints.deviceId = useExactDeviceId ? {exact: options.videoDeviceId} : {ideal: options.videoDeviceId};
		}
		if (resolution) {
			videoConstraints.width = {ideal: resolution.width};
			videoConstraints.height = {ideal: resolution.height};
			videoConstraints.frameRate = resolution.frameRate;
		}
		let audioConstraints: MediaTrackConstraints | false = false;
		if (options?.audioDeviceId !== undefined) {
			audioConstraints = {
				echoCancellation: false,
				noiseSuppression: false,
				autoGainControl: false,
			};
			if (options.audioDeviceId && options.audioDeviceId !== 'default') {
				audioConstraints.deviceId = useExactDeviceId ? {exact: options.audioDeviceId} : {ideal: options.audioDeviceId};
			}
		}
		return {
			video: videoConstraints,
			audio: audioConstraints,
		};
	}
	private async createDeviceReplacementTracks(
		options?: DeviceScreenShareCaptureOptions,
	): Promise<CapturedScreenShareTracks> {
		const exactConstraints = this.getDeviceMediaConstraints(options, true);
		let stream: MediaStream;
		try {
			stream = await navigator.mediaDevices.getUserMedia(exactConstraints);
		} catch (error) {
			if (!options?.videoDeviceId && !options?.audioDeviceId) {
				throw error;
			}
			stream = await navigator.mediaDevices.getUserMedia(this.getDeviceMediaConstraints(options, false));
		}
		const videoTrack = stream.getVideoTracks()[0];
		if (!videoTrack) {
			stream.getTracks().forEach(stopMediaTrack);
			throw new Error('No video track found in device screen share capture');
		}
		return {
			videoTrack,
			audioTrack: stream.getAudioTracks()[0],
		};
	}
	private async replaceActiveScreenShareAudioTrack(
		participant: LocalParticipant,
		audioTrack: MediaStreamTrack | undefined,
	): Promise<boolean> {
		const screenShareAudioPublication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		const existingAudioTrack =
			screenShareAudioPublication?.audioTrack ?? (screenShareAudioPublication?.track as LocalAudioTrack | undefined);
		if (audioTrack) {
			if (existingAudioTrack) {
				await existingAudioTrack.replaceTrack(audioTrack, false);
				return true;
			}
			await participant.publishTrack(audioTrack, {
				source: Track.Source.ScreenShareAudio,
				stream: 'screen_share',
			});
			return true;
		}
		if (existingAudioTrack) {
			await participant.unpublishTrack(existingAudioTrack);
		}
		return false;
	}
	async ensureLinuxScreenShareAudioPublication(room: Room | null): Promise<boolean> {
		const participant = room?.localParticipant;
		if (!participant || !participant.isScreenShareEnabled) return false;
		const existing = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		const existingAudioTrack = existing?.audioTrack ?? (existing?.track as LocalAudioTrack | undefined);
		if (existingAudioTrack) return true;
		const capturedTrack = await captureVirtmicAudioTrack();
		if (!capturedTrack) return false;
		try {
			await participant.publishTrack(capturedTrack, {
				source: Track.Source.ScreenShareAudio,
				stream: 'screen_share',
			});
		} catch (error) {
			logger.warn('Failed to publish mid-stream Linux screen-share audio track', {error});
			try {
				capturedTrack.stop();
			} catch {}
			return false;
		}
		this.syncLocalScreenShareAudioState(participant, true);
		this.syncPersistedScreenShareAudioPreference(participant);
		return true;
	}
	private async refreshScreenShareSimulcastTracks(screenShareTrack: LocalVideoTrack): Promise<void> {
		const simulcastCodecs = (
			screenShareTrack as LocalVideoTrack & {
				simulcastCodecs?: Map<unknown, SimulcastTrackInfoLike>;
			}
		).simulcastCodecs;
		if (!simulcastCodecs?.size) {
			return;
		}
		for (const simulcastTrackInfo of simulcastCodecs.values()) {
			const previousTrack = simulcastTrackInfo.mediaStreamTrack;
			let nextTrack: MediaStreamTrack | undefined;
			try {
				nextTrack = screenShareTrack.mediaStreamTrack.clone();
				await simulcastTrackInfo.sender?.replaceTrack(nextTrack);
				simulcastTrackInfo.mediaStreamTrack = nextTrack;
			} catch (error) {
				stopMediaTrack(nextTrack);
				try {
					await simulcastTrackInfo.sender?.replaceTrack(null);
				} catch (replaceError) {
					logger.warn('Failed to stop stale screen share simulcast track after replacement failed', {
						error: replaceError,
					});
				}
				logger.warn('Failed to replace active screen share simulcast track', {error});
			} finally {
				stopMediaTrack(previousTrack);
			}
		}
	}
	private async replaceActiveScreenShareTracks(
		room: Room,
		participant: LocalParticipant,
		tracks: CapturedScreenShareTracks,
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		const screenSharePublication = participant.getTrackPublication(Track.Source.ScreenShare);
		const screenShareTrack = screenSharePublication?.videoTrack;
		if (!screenShareTrack) {
			stopMediaTrack(tracks.videoTrack);
			stopMediaTrack(tracks.audioTrack);
			logger.warn('No active screen share video track to replace');
			return false;
		}
		try {
			await screenShareTrack.replaceTrack(tracks.videoTrack, false);
			await this.refreshScreenShareSimulcastTracks(screenShareTrack);
		} catch (error) {
			stopMediaTrack(tracks.videoTrack);
			stopMediaTrack(tracks.audioTrack);
			logger.error('Failed to replace active screen share video track', {error});
			return false;
		}
		let audioTrackAdopted = false;
		try {
			audioTrackAdopted = await this.replaceActiveScreenShareAudioTrack(participant, tracks.audioTrack);
		} catch (error) {
			if (!audioTrackAdopted) {
				stopMediaTrack(tracks.audioTrack);
			}
			logger.warn('Failed to replace active screen share audio track', {error});
		}
		await this.updateActiveScreenShareSettings(room, options, publishOptions);
		this.applyScreenShareContentHint(participant);
		VoiceMediaStateCoordinator.applyScreenShareState(true, {reason: 'user', sendUpdate: true});
		updateLocalParticipantFromRoom(room);
		this.syncLocalStreamWatchState(true);
		this.syncLocalScreenShareAudioState(participant, true);
		this.syncPersistedScreenShareAudioPreference(participant);
		logger.info('Replaced active screen share source', {
			audioIncluded: tracks.audioTrack != null,
		});
		return true;
	}
	async setScreenShareEnabled(
		room: Room | null,
		enabled: boolean,
		options?: ScreenShareCaptureOptions & {
			sendUpdate?: boolean;
			playSound?: boolean;
			restartIfEnabled?: boolean;
		},
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share not supported on native');
			return;
		}
		const {sendUpdate = true, playSound = true, restartIfEnabled = false, ...restOptions} = options || {};
		const participant = room?.localParticipant;
		if (!participant) {
			logger.warn('No participant');
			return;
		}
		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring request');
			return;
		}
		if (enabled && restartIfEnabled && participant.isScreenShareEnabled) {
			await this.setScreenShareEnabled(room, false, {
				sendUpdate: false,
				playSound: false,
			});
			await this.setScreenShareEnabled(
				room,
				true,
				{
					...restOptions,
					sendUpdate,
					playSound,
				},
				publishOptions,
			);
			return;
		}
		if (enabled && isDesktop() && isNativeMacOS()) {
			const denied = MediaPermission.isScreenRecordingExplicitlyDenied();
			if (denied) {
				logger.warn('Screen recording permission explicitly denied');
				throw new ScreenRecordingPermissionDeniedError();
			}
			const nativeResult = await ensureNativePermission('screen');
			if (nativeResult === 'denied') {
				logger.warn('Screen recording permission denied');
				MediaPermission.markScreenRecordingExplicitlyDenied();
				throw new ScreenRecordingPermissionDeniedError();
			}
			if (nativeResult === 'granted') {
				MediaPermission.updateScreenRecordingPermissionGranted();
			}
		}
		const stateReason = sendUpdate ? 'user' : 'server';
		const applyState = (value: boolean) => {
			VoiceMediaStateCoordinator.applyScreenShareState(value, {reason: stateReason, sendUpdate});
		};
		if (!enabled) applyState(false);
		runInAction(() => {
			this.isScreenSharePending = true;
		});
		const effectivePublishOptions = this.getEffectivePublishOptions(enabled, publishOptions);
		try {
			await participant.setScreenShareEnabled(enabled, restOptions, effectivePublishOptions);
			if (enabled) {
				applyState(true);
				this.applyScreenShareContentHint(participant);
			} else {
				await this.cleanupLingeringScreenShareAudio(participant);
			}
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(enabled);
			this.syncLocalScreenShareAudioState(participant, enabled);
			if (enabled) {
				this.syncPersistedScreenShareAudioPreference(participant);
			}
			if (playSound) {
				if (enabled) {
					SoundCommands.playSound(SoundType.ScreenShareStart);
				} else {
					SoundCommands.playSound(SoundType.ScreenShareStop);
				}
			}
			logger.info('Success', {enabled});
		} catch (e) {
			if (e instanceof Error && (e.name === 'AbortError' || e.name === 'NotAllowedError')) {
				logger.debug('User cancelled or permission denied', {name: e.name});
				const actual = participant.isScreenShareEnabled;
				if (!actual) {
					await this.cleanupLingeringScreenShareAudio(participant);
				}
				applyState(actual);
				updateLocalParticipantFromRoom(room);
				this.syncLocalStreamWatchState(actual);
				this.syncLocalScreenShareAudioState(participant, actual);
				return;
			}
			logger.error('Failed', {enabled, error: e});
			const actual = participant.isScreenShareEnabled;
			if (!actual) {
				await this.cleanupLingeringScreenShareAudio(participant);
			}
			applyState(actual);
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(actual);
			this.syncLocalScreenShareAudioState(participant, actual);
			if (playSound) {
				if (actual) {
					SoundCommands.playSound(SoundType.ScreenShareStart);
				} else {
					SoundCommands.playSound(SoundType.ScreenShareStop);
				}
			}
		} finally {
			runInAction(() => {
				this.isScreenSharePending = false;
			});
		}
	}
	async startDeviceScreenShare(
		room: Room | null,
		options?: DeviceScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share not supported on native');
			return;
		}
		const {sendUpdate = true, playSound = true, videoDeviceId, audioDeviceId, resolution} = options || {};
		const participant = room?.localParticipant;
		if (!participant) {
			logger.warn('No participant');
			return;
		}
		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring device share request');
			return;
		}
		if (participant.isScreenShareEnabled) {
			await this.setScreenShareEnabled(room, false, {
				sendUpdate: false,
				playSound: false,
			});
		}
		const stateReason = sendUpdate ? 'user' : 'server';
		const applyState = (value: boolean) => {
			VoiceMediaStateCoordinator.applyScreenShareState(value, {reason: stateReason, sendUpdate});
		};
		runInAction(() => {
			this.isScreenSharePending = true;
		});
		const effectivePublishOptions = this.getEffectivePublishOptions(true, publishOptions);
		const createdTracks: Array<LocalAudioTrack | LocalVideoTrack> = [];
		const publishedTracks: Array<LocalAudioTrack | LocalVideoTrack> = [];
		try {
			const nativeCameraResult = await ensureNativePermission('camera');
			if (nativeCameraResult === 'denied') {
				MediaPermission.markCameraExplicitlyDenied();
				ModalCommands.push(modal(() => <CameraPermissionDeniedModal />));
				throw Object.assign(new Error('Camera permission denied'), {name: 'NotAllowedError'});
			}
			if (nativeCameraResult === 'granted') {
				MediaPermission.updateCameraPermissionGranted();
			}
			if (audioDeviceId !== undefined) {
				const nativeMicrophoneResult = await ensureNativePermission('microphone');
				if (nativeMicrophoneResult === 'denied') {
					MediaPermission.markMicrophoneExplicitlyDenied();
					ModalCommands.push(modal(() => <MicrophonePermissionDeniedModal />));
					throw Object.assign(new Error('Microphone permission denied'), {name: 'NotAllowedError'});
				}
				if (nativeMicrophoneResult === 'granted') {
					MediaPermission.updateMicrophonePermissionGranted();
				}
			}
			const videoTrack = await createLocalVideoTrack({
				deviceId: videoDeviceId && videoDeviceId !== 'default' ? videoDeviceId : undefined,
				resolution: resolution
					? {
							width: resolution.width,
							height: resolution.height,
							frameRate: resolution.frameRate,
						}
					: undefined,
			});
			createdTracks.push(videoTrack);
			let audioTrack: LocalAudioTrack | undefined;
			if (audioDeviceId !== undefined) {
				audioTrack = await createLocalAudioTrack({
					deviceId: audioDeviceId || undefined,
					echoCancellation: false,
					noiseSuppression: false,
					autoGainControl: false,
					voiceIsolation: false,
				});
				createdTracks.push(audioTrack);
			}
			await participant.publishTrack(videoTrack, {
				...effectivePublishOptions,
				source: Track.Source.ScreenShare,
				stream: 'screen_share',
			});
			publishedTracks.push(videoTrack);
			if (audioTrack) {
				await participant.publishTrack(audioTrack, {
					source: Track.Source.ScreenShareAudio,
					stream: 'screen_share',
				});
				publishedTracks.push(audioTrack);
			}
			applyState(true);
			this.applyScreenShareContentHint(participant);
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(true);
			this.syncLocalScreenShareAudioState(participant, true);
			if (playSound) {
				SoundCommands.playSound(SoundType.ScreenShareStart);
			}
			logger.info('Started device screen share', {
				videoDeviceId,
				audioIncluded: audioTrack != null,
			});
		} catch (error) {
			if (publishedTracks.length > 0) {
				await Promise.allSettled(publishedTracks.map((track) => participant.unpublishTrack(track)));
			}
			createdTracks.forEach((track) => {
				track.stop();
			});
			const actual = participant.isScreenShareEnabled;
			applyState(actual);
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(actual);
			this.syncLocalScreenShareAudioState(participant, actual);
			if (
				error instanceof Error &&
				error.name !== 'AbortError' &&
				error.name !== 'NotAllowedError' &&
				error.name !== 'PermissionDeniedError'
			) {
				logger.error('Failed to start device screen share', {
					error,
					videoDeviceId,
					audioIncluded: audioDeviceId != null,
				});
			}
		} finally {
			runInAction(() => {
				this.isScreenSharePending = false;
			});
		}
	}
	async replaceActiveDisplayScreenShare(
		room: Room | null,
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share source switching is not supported on native');
			return false;
		}
		const participant = room?.localParticipant;
		if (!participant || !participant.isScreenShareEnabled) {
			logger.warn('No active screen share to replace');
			return false;
		}
		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring screen share source switch');
			return false;
		}
		runInAction(() => {
			this.isScreenSharePending = true;
		});
		try {
			const tracks = await this.createDisplayScreenShareTracks(options);
			return await this.replaceActiveScreenShareTracks(room, participant, tracks, options, publishOptions);
		} catch (error) {
			if (
				error instanceof Error &&
				(error.name === 'AbortError' || error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')
			) {
				logger.debug('User cancelled or denied screen share source switch', {name: error.name});
			} else {
				logger.error('Failed to replace active display screen share source', {error});
			}
			return false;
		} finally {
			runInAction(() => {
				this.isScreenSharePending = false;
			});
		}
	}
	async replaceActiveDeviceScreenShare(
		room: Room | null,
		options?: DeviceScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share source switching is not supported on native');
			return false;
		}
		const participant = room?.localParticipant;
		if (!participant || !participant.isScreenShareEnabled) {
			logger.warn('No active screen share to replace');
			return false;
		}
		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring device share source switch');
			return false;
		}
		runInAction(() => {
			this.isScreenSharePending = true;
		});
		try {
			const nativeCameraResult = await ensureNativePermission('camera');
			if (nativeCameraResult === 'denied') {
				MediaPermission.markCameraExplicitlyDenied();
				ModalCommands.push(modal(() => <CameraPermissionDeniedModal />));
				throw Object.assign(new Error('Camera permission denied'), {name: 'NotAllowedError'});
			}
			if (nativeCameraResult === 'granted') {
				MediaPermission.updateCameraPermissionGranted();
			}
			if (options?.audioDeviceId !== undefined) {
				const nativeMicrophoneResult = await ensureNativePermission('microphone');
				if (nativeMicrophoneResult === 'denied') {
					MediaPermission.markMicrophoneExplicitlyDenied();
					ModalCommands.push(modal(() => <MicrophonePermissionDeniedModal />));
					throw Object.assign(new Error('Microphone permission denied'), {name: 'NotAllowedError'});
				}
				if (nativeMicrophoneResult === 'granted') {
					MediaPermission.updateMicrophonePermissionGranted();
				}
			}
			const tracks = await this.createDeviceReplacementTracks(options);
			return await this.replaceActiveScreenShareTracks(room, participant, tracks, undefined, publishOptions);
		} catch (error) {
			if (
				error instanceof Error &&
				(error.name === 'AbortError' || error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')
			) {
				logger.debug('User cancelled or denied device share source switch', {name: error.name});
			} else {
				logger.error('Failed to replace active device screen share source', {
					error,
					videoDeviceId: options?.videoDeviceId,
					audioIncluded: options?.audioDeviceId != null,
				});
			}
			return false;
		} finally {
			runInAction(() => {
				this.isScreenSharePending = false;
			});
		}
	}
	async updateActiveScreenShareSettings(
		room: Room | null,
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share updates are not supported on native');
			return false;
		}
		const participant = room?.localParticipant;
		if (!participant || !participant.isScreenShareEnabled) {
			return false;
		}
		const screenSharePublication = participant.getTrackPublication(Track.Source.ScreenShare);
		const screenShareTrack = screenSharePublication?.videoTrack;
		if (!screenShareTrack) {
			logger.warn('No active screen share track to update');
			return false;
		}
		let appliedAnySetting = false;
		const resolution = options?.resolution;
		if (resolution) {
			const nextConstraints: MediaTrackConstraints = {};
			if (resolution.width > 0) {
				nextConstraints.width = {ideal: resolution.width};
			}
			if (resolution.height > 0) {
				nextConstraints.height = {ideal: resolution.height};
			}
			if (resolution.frameRate !== undefined) {
				nextConstraints.frameRate = resolution.frameRate;
			}
			try {
				await screenShareTrack.mediaStreamTrack.applyConstraints(nextConstraints);
				appliedAnySetting = true;
			} catch (error) {
				logger.warn('Failed to update active screen share constraints', {
					error,
					resolution,
				});
			}
		}
		if (options?.contentHint) {
			screenShareTrack.mediaStreamTrack.contentHint = options.contentHint;
			appliedAnySetting = true;
		}
		const screenShareEncoding = publishOptions?.screenShareEncoding;
		if (screenShareEncoding) {
			const sender = screenShareTrack.sender;
			if (!sender) {
				logger.warn('No sender found for active screen share track');
			} else {
				const senderParameters = sender.getParameters();
				const encodings = senderParameters.encodings?.length ? senderParameters.encodings : [{}];
				senderParameters.encodings = encodings.map((encoding) => {
					const nextEncoding = {...encoding};
					if (screenShareEncoding.maxFramerate !== undefined) {
						nextEncoding.maxFramerate = screenShareEncoding.maxFramerate;
					} else {
						delete nextEncoding.maxFramerate;
					}
					if (screenShareEncoding.maxBitrate !== undefined) {
						nextEncoding.maxBitrate = screenShareEncoding.maxBitrate;
					} else {
						delete nextEncoding.maxBitrate;
					}
					return nextEncoding;
				});
				try {
					await sender.setParameters(senderParameters);
					appliedAnySetting = true;
				} catch (error) {
					logger.warn('Failed to update active screen share sender parameters', {
						error,
						screenShareEncoding,
					});
				}
			}
		}
		if (typeof options?.audio === 'boolean') {
			const screenShareAudioPublication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
			if (screenShareAudioPublication) {
				try {
					if (options.audio) {
						await screenShareAudioPublication.unmute();
					} else {
						await screenShareAudioPublication.mute();
					}
					appliedAnySetting = true;
				} catch (error) {
					logger.warn('Failed to update active screen share audio state', {
						error,
						includeAudio: options.audio,
					});
				}
			} else if (options.audio) {
				logger.info('Cannot enable screen share audio without restarting screen share');
			}
		}
		updateLocalParticipantFromRoom(room);
		this.syncLocalScreenShareAudioState(participant, participant.isScreenShareEnabled);
		return appliedAnySetting;
	}
	async toggleScreenShareFromKeybind(room: Room | null): Promise<void> {
		const current = LocalVoiceState.getSelfStream();
		await this.setScreenShareEnabled(room, !current);
	}
	resetStreamTracking(): void {
		runInAction(() => {
			this.isScreenSharePending = false;
		});
	}
}

export default new VoiceScreenShareManager();
